import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  PlusCircleFilled,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Modal,
  Table,
  Form,
  Select,
  Radio,
  InputNumber,
  Alert,
  Input,
} from "antd";
import { DateTime } from "luxon";
import React, { useState } from "react";
import { useRecoilState } from "recoil";
import TimePicker from "../../components/TimePicker/TimePicker";
import { InfusionActionType } from "../../enums/infusion.enum";
import { sharedInternalInfoState } from "../../recoil/atoms/sharedInternalAtom";
import InfusionService from "../../services/infusions.service";
import { AbnormalFilter } from "../../types/common.types";
import { Infusion } from "../../types/infusions.type";
import styles from "../../index.style";
// import "./Infusions.css";
import { customTableTitle } from "../../utils/tables.utils";
import { userInfoState } from "../../recoil/atoms/userInfoAtom";
import { useTranslation } from "react-i18next";
import { changeShowLoading } from "../../components/Spinner/Spinner";
import MyDatePicker from "../../components/DatePicker/DatePicker";

function Infusions() {
  const { t, i18n } = useTranslation();
  const [userInfo, setUserInfo] = useRecoilState(userInfoState);
  const [tableData, setTableData] = useState<Infusion[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [infusionTypeList, setInfusionTypeList] = useState([]);
  const [drugsList, setDrugsList] = useState([]);
  const [measureUnitList, setMeasureUnitList] = useState([]);
  const [sharedInfo, setSharedInfo] = useRecoilState(sharedInternalInfoState);
  const [paginationLoadType, setPaginationLoadType] = useState(50);
  const [paginationLoadDrug, setPaginationLoadDrug] = useState(50);

  const [timePickerValue, setTimePickerValue] = useState<string | null>(null);
  const [deletingRow, setDeletingRow] = useState(false);
  const [mode, setMode] = useState(InfusionActionType.NONE);
  const [selectedRowIndex, setSelectedRowIndex] = useState(-1);
  const [drugTypeValue, setDrugTypeValue] = useState("");
  const [filterOption, setFilterOption] = useState<{ label: string; value: string }[]>([]);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [searchString, setSearchString] = useState<string>("");
  const [filteredTableData, setFilteredTableData] = useState<Infusion[]>([]);
  const [pagination, setPagination] = useState<{
    page: number;
    pageSize: number;
  }>({ page: 1, pageSize: 10 });
  const [currentRow, setCurrentRow] = useState<{
    rowIndex: number;
    event: any;
    record: any;
  }>();

  const [currentStatus, setCurrentStatus] = useState("");
  const isDisabled = currentStatus === "validated";

  const [form] = Form.useForm();
  const labelColSpan = 16;
  const wrapperColSpan = 8;
  const columns = [
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (text: any, record: any, rowIndex: number) => (
        <div className="flex items-center gap-2">
          <Button
            type="primary"
            icon={<SearchOutlined />}
            style={{ backgroundColor: styles.blueColor, padding: "7px" }}
            className="customButtonNoFloat"
            onClick={() => editSelectedInfusion(record, rowIndex)}
          />
          <Button
            type="primary"
            icon={<CopyOutlined />}
            style={{ backgroundColor: styles.blueColor, padding: "7px" }}
            className="customButtonNoFloat"
            onClick={() => duplicateRow(record)}
            disabled={isDisabled}
          />
        </div>
      ),
    },
    {
      title: customTableTitle(`${t("DRUG")}`, ""),
      dataIndex: "drug",
      key: "drug",
      render: (text: any, record: any) => (
        <div>
          <span>
            {record.infusionInfoType?.code} -{" "}
            {record.infusionInfoType?.description}
          </span>
          <br />
          <span>
            {record.infusionInfo?.code} - {record.infusionInfo?.description}
          </span>
        </div>
      ),
    },
    {
      title: customTableTitle(`${t("ASSURED")}`, ""),
      dataIndex: "checked",
      key: "checked",
      render: (text: any, record: any) => (
        <div>
          {record.verified ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
        </div>
      ),
    },
    {
      title: customTableTitle(`${t("DOSAGE")}`, ""),
      dataIndex: "dosage",
      key: "dosage",
      render: (text: any, record: any) => (
        <div>
          <p>
            {record?.dosage}{" "}
            {record?.measureInfo?.description
              ? record?.measureInfo?.description
              : ""}
          </p>
        </div>
      ),
    },
    {
      title: customTableTitle(`${t("DILUITION")}`, ""),
      dataIndex: "diluition",
      key: "diluition",
    },
    {
      title: customTableTitle(`${t("SPEED")}`, ""),
      dataIndex: "speed",
      key: "speed",
      render: (text: any, record: any) => (
        <div>
          <p>
            {record?.speed}{" "}
            {record?.measureSpeedInfo?.description
              ? record?.measureSpeedInfo?.description
              : ""}
          </p>
        </div>
      ),
    },
    {
      title: customTableTitle(`${t("START")}`, ""),
      dataIndex: "startDate",
      key: "start",
      render: (date: string) =>
        date
          ? DateTime.fromISO(date, {
            zone: "Europe/Rome", //TODO: Da scodare
          }).toLocaleString(DateTime.DATETIME_MED)
          : null,
    },
    {
      title: customTableTitle(`${t("END")}`, ""),
      dataIndex: "endDate",
      key: "end",
      render: (date: string) =>
        date
          ? DateTime.fromISO(date, {
            zone: "Europe/Rome", //TODO: Da scodare
          }).toLocaleString(DateTime.DATETIME_MED)
          : null,
    },
    {
      title: " ",
      dataIndex: "actions",
      key: "actions",
      render: (text: any, record: any, rowIndex: number) => (
        <div>
          <Button
            type="primary"
            icon={<DeleteOutlined />}
            style={{ backgroundColor: styles.red, padding: "7px" }}
            className="customButtonNoFloat"
            onClick={() => prepareToDeleteInfusion(record)}
            disabled={isDisabled}
          />
        </div>
      ),
    },
  ];

  const duplicateRow = (record: Infusion) => {
    const newRow = {
      verified: record.verified,
      dosage: record.dosage,
      measureUnit: record.measureInfo?.uuid,
      measureSpeedUnit: record.measureSpeedInfo?.uuid,
      speed: record.speed,
      drug: record.infusionInfo.uuid,
      diluition: record.diluition,
      startDate: DateTime.fromISO(record.startDate, {
        zone: "Europe/Rome", //TODO: Da scodare
      }).toFormat("yyyy-MM-dd HH:mm:ss"),
      endDate:
        record.endDate &&
        DateTime.fromISO(record.endDate, {
          zone: "Europe/Rome", //TODO: Da scodare
        }).toFormat("yyyy-MM-dd HH:mm:ss"),

    };

    // Non posso usare mode (InfusionActionType.DUPLICATE) perchè è asyncronous
    updateInfusionList(newRow, "duplicate");

  };

  const prepareToDeleteInfusion = (record: Infusion) => {
    if (
      userInfo.capabilities["CAN_EDIT_ALL_TAB"] ||
      userInfo.capabilities["CAN_EDIT_INFUSIONS_TAB"]
    ) {
      setMode(InfusionActionType.DELETE);
      const data = tableData.filter((item) => item !== record);
      setTableData(data);
      setDeletingRow(true);
    } else {
      <Alert
        message={t("PERMISSION_NEGATE")}
        type="warning"
        showIcon
        closable
      />;
    }
  };

  const loadData = () => {
    const payload = {
      uuid: sharedInfo.surgery,
      tabName: "infusions-tab",
    };
    changeShowLoading(
      InfusionService.loadData(payload)
        .then((response) => {
          console.log(response.infusion);
          setTableData(response.infusion);
          setCurrentStatus(response?.currentStatus || "");
        })
        .catch((error) => {
          console.error(error);
        })
    );
  };


  React.useEffect(() => {
    if (selectedItem) {
      const filteredData = tableData.filter((item: Infusion) => {
        const matchesSelected =
          !selectedItem || item.infusionInfo?.description === selectedItem;
        return matchesSelected;
      });
      setFilteredTableData(filteredData);
    }

    const uniqueOptions = Array.from(
      new Map(
        tableData.map((item: Infusion) => [
          item.infusionInfo?.description,
          {
            label: item.infusionInfo?.description,
            value: item.infusionInfo?.description,
          },
        ])
      ).values()
    );

    setFilterOption(uniqueOptions as { label: string; value: string }[]);
  }, [selectedItem, tableData]);


  const addInfusion = () => {
    setIsModalOpen(true);
    setMode(InfusionActionType.ADD);
  };

  const prepareUploadPayload = () => {
    console.log("prepareUploadPayload");
    console.log(tableData);
    return (tableData || []).map((item) => {
      return {
        verified: item.verified,
        dosage: item.dosage,
        measureUnit: item.measureInfo?.uuid,
        measureSpeedUnit: item.measureSpeedInfo?.uuid,
        speed: item.speed,
        drug: item.infusionInfo.uuid,
        diluition: item.diluition,
        startDate: DateTime.fromISO(item.startDate, {
          zone: "Europe/Rome", //TODO: Da scodare
        }).toFormat("yyyy-MM-dd HH:mm:ss"),
        endDate:
          item.endDate &&
          DateTime.fromISO(item.endDate, {
            zone: "Europe/Rome", //TODO: Da scodare
          }).toFormat("yyyy-MM-dd HH:mm:ss"),
      };
    });
  };

  const handleOk = () => {
    if (
      userInfo.capabilities["CAN_EDIT_ALL_TAB"] ||
      userInfo.capabilities["CAN_EDIT_INFUSIONS_TAB"]
    ) {
      const data = form.getFieldsValue();
      form
        .validateFields()
        .then(() => {
          updateInfusionList(data);
        })
        .catch((error) => {
          console.info("Some fields are missing", error);
        });
    } else {
      <Alert
        message={t("PERMISSION_NEGATE")}
        type="warning"
        showIcon
        closable
      />;
      setIsModalOpen(false);
    }
  };

  const updateInfusionList = (data: any, duplicate?: any) => {
    let payload = prepareUploadPayload();
    if (duplicate) {
      payload.push(data);
    } else {
      console.log("PAYLOAD: ", payload, "DATA: ", data);

      if (mode === InfusionActionType.ADD) {
        // format data
        data.startDate = DateTime.fromISO(data.startDate, {
          zone: "Europe/Rome", //TODO: Da scodare
        }).toFormat("yyyy-MM-dd HH:mm:ss");
        data.endDate =
          data.endDate &&
          DateTime.fromISO(data.endDate, {
            zone: "Europe/Rome", //TODO: Da scodare
          }).toFormat("yyyy-MM-dd HH:mm:ss");
        payload.push(data);
      } else if (mode === InfusionActionType.UPDATE) {
        // format data
        data.startDate = DateTime.fromISO(data.startDate, {
          zone: "Europe/Rome", //TODO: Da scodare
        }).toFormat("yyyy-MM-dd HH:mm:ss");
        data.endDate =
          data.endDate &&
          DateTime.fromISO(data.endDate, {
            zone: "Europe/Rome", //TODO: Da scodare
          }).toFormat("yyyy-MM-dd HH:mm:ss");
        payload[selectedRowIndex] = data;
      }
    }

    changeShowLoading(
      InfusionService.update(sharedInfo.surgery, {
        tabName: "infusions-tab",
        content: payload,
      })
        .then(() => {
          form.resetFields();
          setIsModalOpen(false);
          if (duplicate) {
            loadData();
          }
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setMode(InfusionActionType.NONE);
          setSelectedRowIndex(-1);
        })
    );
  };

  const loadDrugTypeList = (filter: string) => {
    const payload: AbnormalFilter[] = [
      {
        field: "t.validityEnd",
        operator: "isValid",
      },
    ];

    const queryParams = {
      page: 1,
      take: filter === "" ? paginationLoadType : 100000000000,
      searchOn: "code,description",
      searchFor: filter,
    };

    changeShowLoading(
      InfusionService.getDrugsTypes(payload, queryParams)
        .then((response) => {
          setInfusionTypeList(response.list);
        })
        .catch((error) => {
          console.error(error);
        })
    );
  };

  const loadDrugs = (filter: string) => {
    const queryParams = {
      page: 1,
      take: filter !== "" || drugTypeValue ? 100000000000 : paginationLoadDrug,
      searchOn: "code,description",
      searchFor: filter,
    };

    const payload: AbnormalFilter[] = [
      {
        field: "t.validityEnd",
        operator: "isValid",
      },
    ];

    if (drugTypeValue) {
      payload.push({
        field: "t.drug_type",
        operator: "equal",
        value: drugTypeValue,
      });
    }

    InfusionService.getDrugs(payload, queryParams)
      .then((response) => {
        setDrugsList(response.list);
      })
      .catch((error) => {
        console.error(error);
      })
  };

  const loadMeasureUnit = () => {
    const payload: AbnormalFilter[] = [
      {
        field: "t.validityEnd",
        operator: "isValid",
      },
    ];

    const queryParams = {
      page: 1,
      take: 50,
      searchOn: "code,description",
      searchFor: "",
    };

    changeShowLoading(
      InfusionService.getMeasureUnit(payload, queryParams)
        .then((response) => {
          setMeasureUnitList(response.list);
        })
        .catch((error) => {
          console.error(error);
        })
    );
  };

  const handleCancel = () => {
    setDrugTypeValue("");
    form.resetFields();
    setIsModalOpen(false);
    setMode(InfusionActionType.NONE);
    setSelectedRowIndex(-1);
  };

  const onDrugTypeChange = (value: any) => {
    setDrugTypeValue(value);
  };

  const editSelectedInfusion = (record: Infusion | any, rowIndex: number) => {
    // const currentIndex = selectedItem 
    // ? tableData.findIndex((item) => item === filteredTableData[rowIndex])
    // : (pagination.page - 1) * pagination.pageSize + rowIndex;
    // console.log("currentIndex", currentIndex);

    const data = selectedItem ? filteredTableData : tableData;
    const ItemIndex = (pagination.page - 1) * pagination.pageSize + rowIndex;
    const recordInFullList = data[ItemIndex];
    const currentIndex = tableData.findIndex((item) => item === recordInFullList);

    setMode(InfusionActionType.UPDATE);
    setSelectedRowIndex(currentIndex);
    setDrugTypeValue(record.infusionInfoType.uuid);

    form.setFieldsValue({
      verified: record.verified,
      dosage: record.dosage,
      speed: record.speed,
      startDate: DateTime.fromISO(record.startDate, {
        zone: "Europe/Rome", //TODO: Da scodare
      }),
      endDate:
        record.endDate &&
        DateTime.fromISO(record.endDate, {
          zone: "Europe/Rome", //TODO: Da scodare
        }),
      measureUnit: record.measureInfo?.uuid,
      measureSpeedUnit: record.measureSpeedInfo?.uuid,
      drugType: record.infusionInfoType?.uuid,
      drug: record.infusionInfo?.uuid,
      diluition: record.diluition,
    });

    setIsModalOpen(true);
  };

  const onRowClick = (e: any, record: Infusion | any, rowIndex: number) => {
    const payload = {
      event: e,
      record: record,
      rowIndex: rowIndex,
    };

    setCurrentRow(payload);
  };

  React.useEffect(() => {
    loadData();
  }, []);

  React.useEffect(() => {
    if (!isModalOpen) loadData();
  }, [isModalOpen]);

  React.useEffect(() => {
    switch (mode) {
      case InfusionActionType.NONE:
        break;
      case InfusionActionType.ADD:
      case InfusionActionType.UPDATE:
        loadDrugTypeList("");
        loadDrugs("");
        loadMeasureUnit();
        break;
      case InfusionActionType.DELETE:
        updateInfusionList(null);
        break;
      default:
        break;
    }
  }, [mode]);

  React.useEffect(() => {
    loadDrugs("");
  }, [drugTypeValue]);

  const onScroll = (value: any, type: string) => {
    const target = value.target;
    if (type === "farmaco") {
      if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
        setPaginationLoadDrug(paginationLoadDrug + 50);
      }
    } else {
      if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
        setPaginationLoadType(paginationLoadType + 50);
      }
    }
  };

  React.useEffect(() => {
    loadDrugs("");
  }, [paginationLoadDrug]);

  React.useEffect(() => {
    loadDrugTypeList("");
  }, [paginationLoadType]);

  const onSearchValue = (value: any, type: string) => {
    if (type === "farmaco") {
      loadDrugs(value);
    } else {
      loadDrugTypeList(value);
    }
  };

  const setTime = (e: any) => {
    setTimePickerValue(e?.setZone("Europe/Rome").toISO());
  };

  React.useEffect(() => {
    console.log(timePickerValue);
  }, [timePickerValue]);

  const setTypologyValue = (value: any) => {
    let drugType = "";

    drugsList.map((item: any) => {
      if (item.uuid === value) {
        drugType = item.drug_type.uuid;
      }
    });

    const payload: AbnormalFilter[] = [
      {
        field: "t.validityEnd",
        operator: "isValid",
      },
      {
        field: "t.uuid",
        operator: "equal",
        value: drugType,
      },
    ];

    const queryParams = {
      page: 1,
      take: 1000000,
      searchOn: "code,description",
      searchFor: "",
    };

    changeShowLoading(
      InfusionService.getDrugsTypes(payload, queryParams)
        .then((response) => {
          form.setFieldsValue({
            drugType: response.list[0].description,
          });
          setInfusionTypeList(response.list);
        })
        .catch((error) => {
          console.error(error);
        })
    );
  };

  React.useEffect(() => {
    if (!deletingRow) return;
    updateInfusionList(null);
  }, [deletingRow]);

  const onSearch = (value: string): void => {
    setSearchString(value);
    loadDrugs(value);
  };

  const onSelect = (value: any): void => {
    setSearchString(value.value);
    setSelectedItem(value.value);
  };

  const onSearchCleared = (): void => {
    setSearchString("");
    setSelectedItem(null);
  };


  return (
    <>
      <Modal
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        closable={false}
        maskClosable={false}
      >
        <div className="form-container">
          <Form
            name="normal_login"
            className="login-form"
            form={form}
            layout="horizontal"
            labelCol={{
              span: 7,
            }}
            wrapperCol={{
              span: 20,
            }}
            disabled={isDisabled}
          >
            <Form.Item
              name="drugType"
              className="form-container-item"
              label={t("TYPOLOGY")}
              rules={[
                {
                  required: true,
                  message: `${t("REQUIRED_FIELD")}`,
                },
              ]}
            >
              <Select
                showSearch
                className="w-full "
                placeholder={t("TYPOLOGY")}
                optionFilterProp="children"
                fieldNames={{ label: "description", value: "uuid" }}
                onChange={onDrugTypeChange}
                onPopupScroll={(value) => onScroll(value, "tipo")}
                onSearch={(value) => onSearchValue(value, "tipo")}
                filterOption={(input, option: any) =>
                  option.description
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
                options={infusionTypeList}
              />
            </Form.Item>
            <Form.Item
              name="drug"
              className="form-container-item"
              label={t("DRUG")}
              rules={[
                {
                  required: true,
                  message: `${t("REQUIRED_FIELD")}`,
                },
              ]}
            >
              <Select
                showSearch
                className="w-full "
                placeholder={t("DRUG")}
                optionFilterProp="children"
                fieldNames={{ label: "description", value: "uuid" }}
                onChange={(value) => setTypologyValue(value)}
                onPopupScroll={(value) => onScroll(value, "farmaco")}
                onSearch={(value) => onSearchValue(value, "farmaco")}
                filterOption={(input, option: any) =>
                  option.description
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
                options={drugsList}
              />
            </Form.Item>

            <Form.Item
              name="verified"
              label={t("ASSURED")}
              className="form-container-item"
            >
              <Radio.Group
                className="flex justify-start form-container-radio"
                optionType="button"
                buttonStyle="solid"
                size="large"
              >
                <Radio value={false}> {t("NO")} </Radio>
                <Radio value={true}> {t("YES")} </Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="dosage"
              label={t("DOSAGE")}
              className="form-container-item"
            >
              <InputNumber
                size="large"
                type="number"
                stringMode
                min={0}
                placeholder={`${t("DOSAGE")}`}
              />
            </Form.Item>

            <Form.Item
              name="measureUnit"
              className="form-container-item"
              label={t("MEASURE_UNIT")}
            >
              <Select
                showSearch
                className="w-full "
                placeholder={t("MEASURE_UNIT")}
                optionFilterProp="children"
                fieldNames={{ label: "description", value: "uuid" }}
                filterOption={(input, option: any) =>
                  option.description
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
                options={measureUnitList}
              />
            </Form.Item>

            <Form.Item
              name="diluition"
              label={t("DILUITION")}
              className="form-container-item"
            >
              <InputNumber
                size="large"
                type="number"
                min={0}
                placeholder={`${t("DILUITION")}`}
              />
            </Form.Item>

            <Form.Item
              name="speed"
              label={t("SPEED")}
              className="form-container-item"
            >
              <InputNumber
                size="large"
                type="number"
                min={0}
                placeholder={`${t("SPEED")}`}
              />
            </Form.Item>
            <Form.Item
              name="measureSpeedUnit"
              className="form-container-item"
              label={t("MEASURE_SPEED_UNIT_LABEL")}
              labelCol={{
                style: {
                  width: '147px',
                  height: '45px',
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                },
              }}
            >
              <Select
                showSearch
                className="w-full"
                placeholder={t("MEASURE_SPEED_UNIT")}
                optionFilterProp="children"
                fieldNames={{ label: "description", value: "uuid" }}
                filterOption={(input, option: any) =>
                  option.description
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
                options={measureUnitList}
              />
            </Form.Item>
            <Form.Item
              name="startDate"
              className="form-container-item"
              label={t("START_DATE")}
              rules={[
                {
                  required: true,
                  message: `${t("REQUIRED_FIELD")}`,
                },
              ]}
            >
              <MyDatePicker showTime />
            </Form.Item>

            <Form.Item
              name="endDate"
              className="form-container-item"
              label={t("END_DATE")}
            >
              <MyDatePicker showTime />
            </Form.Item>
          </Form>
        </div>
      </Modal>
      <div className="page-container-margin">
        <div
          style={{ borderBottom: "1px solid #EEE" }}
          className="flex w-full justify-beetween items-center border-opacity-30 pb-4 text-left"
        >
          <div className="flex w-4/6 justify-between items-center">
            <Select
              className="w-4/6 mr-4"
              showSearch
              onSearch={onSearch}
              onSelect={onSelect}
              options={filterOption}
              value={searchString}
              placeholder={t("FILTER_INFUSIONS_LIST")}
              labelInValue
              key="infusions-search-filter"
              allowClear
              filterOption={(input, option: any) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              suffixIcon={<SearchOutlined />}
              onClear={onSearchCleared}
            ></Select>
            <div className="w-2/6">
              <Form
                style={{
                  margin: 0,
                  padding: 0,
                }}
              >
                <Input
                  name="barcode"
                  className="w-full pr-3"
                  placeholder={t("TOTAL_DOSAGE")}
                  style={{ width: "200px", color: "#808080", fontWeight: "bold" }}
                  value={`${t("TOTAL_INFUSION_DOSAGE")}: ${selectedItem ?
                    filteredTableData.reduce((total, item) => total + (item.dosage || 0), 0)
                    : tableData.reduce((total, item) => total + (item.dosage || 0), 0)}`}
                  disabled
                />
              </Form>
            </div>
          </div>
          <div className="flex justify-end w-2/6">
            <Button
              type="primary"
              icon={<PlusCircleFilled />}
              className="customButton"
              style={{
                backgroundColor: styles.buttonTeal,
                borderColor: styles.buttonTealHover,
              }}
              onClick={addInfusion}
              disabled={isDisabled}
            >
              {t("ADD_INFUSION")}
            </Button>
          </div>
        </div>
        <div className="sisos-table-container">
          <Table
            size="small"
            className="sisos-table sisos-table-nopadding"
            pagination={{
              pageSize: window.innerWidth > 900 ? 12 : 7,
              showSizeChanger: true,
              hideOnSinglePage: true,
              position: ["topCenter"],
              size: "small",
            }}
            dataSource={selectedItem ? filteredTableData : tableData}
            columns={columns}
            rowClassName="infusion-row"
            onChange={(pagination, filters, sorter, extra) => {
              console.log("params", pagination);
              setPagination({
                page: pagination.current || 1,
                pageSize: pagination.pageSize || 10,
              });
            }}
            onRow={(record, rowIndex) => {
              return {
                onClick: (e) => {
                  if (rowIndex !== undefined) {
                    // editSelectedInfusion(record, rowIndex);
                    onRowClick(e, record, rowIndex);
                  }
                },
              };
            }}
          />
        </div>
      </div>
    </>
  );
}

export default Infusions;
